import { TextSelection } from '@tiptap/pm/state'
import { Editor } from '@tiptap/react'
import { Form, InputNumber, Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  editor: Editor
  onCancel: () => void
}

export const ImageResizeModal = ({ editor, onCancel }: Props) => {
  const { t } = useTranslation()

  const [form] = Form.useForm()
  const [imageNode, setImageNode] = useState<{
    src: string
    alt?: string
    width?: number
    height?: number
    pos: number
  } | null>(null)

  useEffect(() => {
    if (!editor) return

    const { state } = editor
    const { selection } = state

    // Try to find image node at current selection
    let foundImage: {
      node: { attrs: Record<string, unknown>; type: { name: string } }
      pos: number
    } | null = null

    // Check if selection is directly on an image node
    if (selection.from === selection.to) {
      const node = state.doc.nodeAt(selection.from)
      if (node && node.type.name === 'image') {
        foundImage = { node, pos: selection.from }
      }
    }

    // If not found, search around the selection
    if (!foundImage) {
      const $pos = state.selection.$from
      state.doc.nodesBetween(
        Math.max(0, $pos.pos - 200),
        Math.min(state.doc.content.size, $pos.pos + 200),
        (node, pos) => {
          if (node.type.name === 'image') {
            // Prefer image closest to selection
            if (
              !foundImage ||
              Math.abs(pos - $pos.pos) < Math.abs(foundImage.pos - $pos.pos)
            ) {
              foundImage = { node, pos }
            }
          }
        }
      )
    }

    if (foundImage) {
      const attrs = foundImage.node.attrs as {
        src: string
        alt?: string
        width?: number | string
        height?: number | string
      }
      const imageData = {
        src: attrs.src,
        alt: attrs.alt,
        width: attrs.width ? Number(attrs.width) : undefined,
        height: attrs.height ? Number(attrs.height) : undefined,
        pos: foundImage.pos
      }
      setImageNode(imageData)
      form.setFieldsValue({
        width: imageData.width,
        height: imageData.height
      })

      // Select the image node
      editor.chain().focus().setTextSelection(foundImage.pos).run()
    } else {
      setImageNode(null)
    }
  }, [editor, form])

  const onOk = () => {
    form.validateFields().then((values) => {
      if (!imageNode) return

      const { width, height } = values

      // Use transaction to update the existing image node instead of inserting a new one
      const { state, dispatch } = editor.view
      const { tr } = state
      const imagePos = imageNode.pos

      // Find the image node at the stored position
      const node = state.doc.nodeAt(imagePos)
      if (node && node.type.name === 'image') {
        // Update the node attributes
        tr.setNodeMarkup(imagePos, undefined, {
          ...node.attrs,
          width: width || null,
          height: height || null
        })

        // Set selection to the updated image position
        tr.setSelection(TextSelection.near(tr.doc.resolve(imagePos)))

        dispatch(tr)
        editor.view.focus()
      }

      onCancel()
    })
  }

  if (!imageNode) {
    return null
  }

  return (
    <Modal
      title={t('editor.resize_image')}
      open
      onOk={onOk}
      onCancel={onCancel}
      okText={t('editor.apply')}
      cancelText={t('editor.cancel')}
    >
      <Form form={form} layout="vertical">
        <Form.Item label={t('editor.width')} name="width">
          <InputNumber
            min={1}
            placeholder={t('editor.auto')}
            style={{ width: '100%' }}
            addonAfter="px"
          />
        </Form.Item>
        <Form.Item label={t('editor.height')} name="height">
          <InputNumber
            min={1}
            placeholder={t('editor.auto')}
            style={{ width: '100%' }}
            addonAfter="px"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
